// Photo rendering for the HoF card: the queue row's avatar and the review panel's
// preview, which must agree on what a missing photo looks like.
//
// Until re-hosted photos exist for real, the card shows a **dummy image**. Before this,
// neither surface rendered an image at all (both showed `photoEmoji`, and the admin drawer
// rendered the literal text "placeholder — the published card shows <filename>"), which is
// why no photos appeared.

// Mirrors photo_rules.MAX_BYTES in webbackend. Duplicated deliberately: checking here lets
// an absurdly large file fail instantly against the same limit the picker advertises,
// instead of after a round trip. The server still enforces it.
export const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

// Only http(s) and inline images may reach an <img src>. `photo` is in
// EDITABLE_RECORD_FIELDS, so an admin can PATCH it to any string — a `data:text/html` or
// `javascript:` value must never be handed to the DOM.
const USABLE_PHOTO = /^(https?:\/\/|data:image\/)/i;

const isUsablePhotoUrl = value =>
  USABLE_PHOTO.test(String(value == null ? '' : value).trim());

// Deterministic hue from the name, so one student always gets the same dummy rather than
// reshuffling colours on every render and looking like a bug.
const hueFor = seed => {
  const text = String(seed == null ? '' : seed);
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) % 360;
  }
  return hash;
};

// Escaped, because the seed is a student name that ends up inside SVG markup.
const escapeXml = text =>
  String(text == null ? '' : text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const initialOf = seed => {
  const trimmed = String(seed == null ? '' : seed).trim();
  return trimmed ? escapeXml(trimmed[0].toUpperCase()) : '★';
};

// A self-contained data URI, not an external placeholder service: this is an internal admin
// tool, and a dummy that depends on a third party is a dummy that breaks.
const dummyPhoto = (seed = '') => {
  const hue = hueFor(seed);
  const svg = [
    '<svg xmlns="http://www.w3.org/2000/svg" width="160" height="160" viewBox="0 0 160 160">',
    `<rect width="160" height="160" rx="80" fill="hsl(${hue},58%,82%)"/>`,
    `<text x="80" y="80" fill="hsl(${hue},52%,28%)" font-family="Helvetica,Arial,sans-serif"`,
    ' font-size="72" font-weight="600" text-anchor="middle" dominant-baseline="central">',
    initialOf(seed),
    '</text>',
    '</svg>',
  ].join('');
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

// nginx in front of the gateway caps request bodies at 1 MB (measured on testenv39: 900 KB
// reaches the app, 1100 KB gets an nginx 413 HTML page). No ingress sets
// proxy-body-size, so every environment inherits that default. A typical phone photo is
// 2-5 MB, so the common case would fail — and fail as unparseable HTML, which surfaces as a
// generic error.
//
// Rather than depend on an infra change we cannot ship, downscale before upload. The
// backend re-encodes to JPEG anyway (that is how EXIF/GPS is stripped), so a client-side
// re-encode costs nothing that was not already being lost.
const UPLOAD_MAX_EDGE = 1600;
const UPLOAD_TARGET_BYTES = 800 * 1024;

// Longest edge capped at `maxEdge`, aspect ratio preserved, never upscaled.
const targetDimensions = (width, height, maxEdge = UPLOAD_MAX_EDGE) => {
  const w = Math.max(0, Math.round(width) || 0);
  const h = Math.max(0, Math.round(height) || 0);
  if (!w || !h) return { width: 0, height: 0 };
  const longest = Math.max(w, h);
  if (longest <= maxEdge) return { width: w, height: h };
  const scale = maxEdge / longest;
  // Round up so a 1-pixel rounding error cannot produce a 0-dimension canvas.
  return {
    width: Math.max(1, Math.ceil(w * scale)),
    height: Math.max(1, Math.ceil(h * scale)),
  };
};

// The photo HoF has re-hosted for this row, or null. It lives on the row, not on the
// record: a parent's upload reaches `meta.photo` immediately but only reaches
// `record.photo` once a card has been assembled, so an unassembled row owns a real photo
// its record cannot see. That gap is why parent uploads rendered as dummies.
const rehostedPhotoUrl = (row = {}) => {
  const url = row && row.photo && row.photo.url;
  return isUsablePhotoUrl(url) ? String(url).trim() : null;
};

// The best real photo for a row, or null when there is none. It substitutes no dummy, so a
// caller can choose between an image and initials rather than being handed an <img src> it
// must render either way.
//
// The record's own `photo` stays as the fallback for rows that predate re-hosting; on the
// seeded rows it is a bare filename, which `isUsablePhotoUrl` rejects.
const rowPhotoUrl = (row = {}) =>
  rehostedPhotoUrl(row) ||
  (isUsablePhotoUrl(row && row.record && row.record.photo)
    ? String(row.record.photo).trim()
    : null);

// Re-encode `file` to a JPEG small enough to clear the 1 MB proxy cap.
//
// Returns the original file untouched if it is already comfortably small, or if anything
// here fails — a failed optimisation must not block an upload that would have worked. The
// server enforces its own limits regardless.
const compressForUpload = async file => {
  if (!file || file.size <= UPLOAD_TARGET_BYTES) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = targetDimensions(bitmap.width, bitmap.height);
    if (!width || !height) return file;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);
    if (bitmap.close) bitmap.close();

    // Step the quality down until it fits. Two attempts is enough for any phone photo at
    // 1600px; going lower starts to look bad on a card meant to celebrate a child.
    for (const quality of [0.85, 0.7]) {
      // eslint-disable-next-line no-await-in-loop
      const blob = await new Promise(resolve =>
        canvas.toBlob(resolve, 'image/jpeg', quality),
      );
      if (blob && blob.size <= UPLOAD_TARGET_BYTES) {
        return new File([blob], 'photo.jpg', { type: 'image/jpeg' });
      }
      if (blob && quality === 0.7) {
        return new File([blob], 'photo.jpg', { type: 'image/jpeg' });
      }
    }
    return file;
  } catch (e) {
    return file;
  }
};

export { dummyPhoto, rowPhotoUrl, compressForUpload };
